import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Card,
  CardBody,
  Badge,
  Modal,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import AdsService from "../../../services/OrderingServices/AdsService";
import AdFilterCard from "../component/AdFilterCard";
import MediaSkeleton from "../../../Components/QAManagementComponent/Skeleton/MediaSkeleton";

const AdsList = () => {
  const navigate = useNavigate();
  const [adsList, setAdsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [filters, setFilters] = useState({
    searchText: "",
    isActive: "",
    tags: [],
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedAdDetails, setSelectedAdDetails] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);
  const MAX_VISIBLE_CAMPAIGNS = 3;
  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);

  const CDN_BASE_URL = window.APP_CONFIG?.api3?.baseapi || "";

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    fetchAdsList();
  }, [currentPage, pageSize, debouncedFilters]);

  async function fetchAdsList(
    page = currentPage,
    size = pageSize,
    currentFilters = debouncedFilters,
  ) {
    try {
      setLoading(true);
      const response = await AdsService.adsList({
        searchText: currentFilters.searchText,
        isActive:
          currentFilters.isActive === ""
            ? undefined
            : currentFilters.isActive === "true",
        tags:
          currentFilters.tags && currentFilters.tags.length > 0
            ? currentFilters.tags
            : undefined,
        pageNumber: page,
        pageSize: size,
      });

      if (response) {
        setAdsList(response?.data || []);
        setTotalRows(response.totalCount || 0);
      }
    } catch (error) {
      toast.error("Failed to fetch ads");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleCardClick = async (id) => {
    try {
      setPreviewLoading(true);
      setSelectedAdDetails(null); // Clear previous details

      const response = await AdsService.getAdById(id);
      setSelectedAdDetails(response);
      setIsPreviewOpen(true); // Only open modal after data is fetched
    } catch (error) {
      toast.error("Failed to load ad details");
      console.error(error);
      setIsPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      searchText: "",
      isActive: "",
      tags: [],
    });
    setCurrentPage(1);
  };

  const getMediaUrl = (ad) => {
    if (ad.mediaType === "Video" && ad.thumbnailFile) {
      return `${CDN_BASE_URL}${ad.thumbnailFile}`;
    }
    return `${CDN_BASE_URL}${ad.mediaPath}`;
  };

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handleDeleteAd = async () => {
    if (!selectedAd) return;

    try {
      setLoading(true);
      await AdsService.delete(selectedAd.id);

      toast.success("Media deleted successfully");
      setIsOpenDelete(false);
      setSelectedAd(null);

      // Refresh list
      fetchAdsList(1, pageSize, debouncedFilters);
      setCurrentPage(1);
    } catch (error) {
      toast.error("Failed to delete media");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const MediaCard = ({ ad }) => {
    return (
      <Card
        className="media-card h-100 border-0"
        style={{ cursor: "pointer" }}
        onClick={() => handleCardClick(ad.id)}
      >
        <div className="media-preview position-relative">
          <img
            loading="lazy"
            src={getMediaUrl(ad)}
            alt={ad.title}
            className="w-100"
            style={{
              height: "180px",
              objectFit: "cover",
              backgroundColor: "#f8f9fa",
            }}
            onError={(e) => {
              e.target.src =
                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-family='sans-serif' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E";
            }}
          />
          <div className="position-absolute top-0 end-0 p-2">
            <Badge className="tag-badge">
              {ad.isActive ? "Active" : "Inactive"}
            </Badge>
          </div>

          <div className="position-absolute top-0 start-0 p-2">
            <Badge className="tag-badge">{ad.mediaType}</Badge>
          </div>
        </div>
        <CardBody className="px-3 py-3 pb-0">
          <div className="d-flex justify-content-between align-items-center mb-2 align-content-center ">
            <h6
              className="mb-0 text-truncate flex-grow-1 me-2"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              {ad.title}({ad.durationSeconds}s)
            </h6>

            <div className="d-flex align-items-center align-content-center gap-2">
              <Button
                size="sm"
                className="icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/ads/${ad.id}`);
                }}
              >
                <i className="ri-edit-line"></i>
              </Button>

              <Button
                size="sm"
                className="icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAd(ad);
                  setIsOpenDelete(true);
                }}
              >
                <i className="ri-delete-bin-line"></i>
              </Button>
            </div>
          </div>

          <div className="text-muted mt-2" style={{ fontSize: "11px" }}>
            {ad.advertiserName}
            {ad.size ? <span className="ms-2">• {ad.size} MB</span> : null}
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <React.Fragment>
      <style>{`
        .media-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-radius: 10px;
          overflow: hidden;
        }



        .media-preview {
          overflow: hidden;
          background: #f8f9fa;
          position: relative;
        }

        .media-preview img {
          transition: transform 0.3s ease;
        }

        .media-card:hover .media-preview img {
          transform: scale(1.05);
        }

        .tag-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 20px;
          background: linear-gradient(
            135deg,
            rgba(255, 140, 0, 0.9),
            rgba(255, 165, 0, 0.85)
          );
          color: #fff;
          backdrop-filter: blur(6px);
          box-shadow: 0 3px 8px rgba(255, 140, 0, 0.35);
          letter-spacing: 0.3px;
        }

        .icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.25s ease;
          background-color: #f8f9fa;
          border: none;
        }

        .icon-btn:hover {
          background-color: #435ebe;
          color: #fff;
          transform: scale(1.1);
        }

        @media (min-width: 992px) {
          .five-col {
            flex: 0 0 20%;
            max-width: 20%;
          }
        }
      `}</style>

      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Media Library" pageTitle="Ads Management" />
        </div>
        <Container fluid>
          {/* Filter Card */}
          <AdFilterCard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleClearFilters={handleClearFilters}
            setCurrentPage={setCurrentPage}
            onAddNewClick={() => navigate("/ads/new")}
          />

          {loading && !adsList.length ? (
            <Row className="g-3">
              {[...Array(12)].map((_, index) => (
                <Col key={index} xs="12" md="6" lg="2">
                  <MediaSkeleton />
                </Col>
              ))}
            </Row>
          ) : adsList.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No ads found</p>
            </div>
          ) : (
            <>
              <Row className="g-3">
                {adsList.map((ad) => (
                  <Col
                    key={ad.id}
                    xs="12"
                    md="6"
                    lg="2"
                    // style={{ flex: "0 0 20%", maxWidth: "20%" }}
                  >
                    <MediaCard ad={ad} />
                  </Col>
                ))}
              </Row>

              {adsList.length < totalRows && (
                <Row className="mt-4">
                  <Col className="text-center">
                    <Button
                      color="primary"
                      outline
                      onClick={handleLoadMore}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Load More"}
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Container>
      </div>

      <Modal
        isOpen={isPreviewOpen}
        toggle={() => setIsPreviewOpen(false)}
        size="xl"
        centered
        contentClassName="border-0 shadow-lg"
      >
        <div className="modal-header border-0 pb-0">
          <button
            type="button"
            className="btn-close"
            onClick={() => setIsPreviewOpen(false)}
            aria-label="Close"
          />
        </div>

        <div className="modal-body p-0">
          {previewLoading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: "400px" }}
            >
              <Triangle color="#435ebe" height={60} width={60} />
            </div>
          ) : selectedAdDetails ? (
            <Row className="g-0">
              {/* LEFT SIDE PREVIEW */}
              <Col md="7" className="bg-light">
                <div
                  className="p-4 d-flex align-items-center justify-content-center"
                  style={{ minHeight: "500px" }}
                >
                  {selectedAdDetails.mediaType === "Video" ? (
                    <video
                      src={`${CDN_BASE_URL}${selectedAdDetails.mediaPath}`}
                      controls
                      autoPlay
                      className="w-100 shadow-sm"
                      style={{
                        borderRadius: "4px",
                        maxHeight: "500px",
                      }}
                    />
                  ) : (
                    <img
                      src={`${CDN_BASE_URL}${selectedAdDetails.mediaPath}`}
                      alt={selectedAdDetails.title}
                      className="w-100 shadow-sm"
                      style={{
                        borderRadius: "4px",
                        objectFit: "contain",
                        maxHeight: "500px",
                      }}
                    />
                  )}
                </div>
              </Col>

              {/* RIGHT SIDE DETAILS */}
              <Col md="5">
                <div className="p-4">
                  <h3
                    className="mb-1 fw-semibold"
                    style={{ fontSize: "1.5rem", color: "#1a1a1a" }}
                  >
                    {selectedAdDetails.title}
                  </h3>

                  <p
                    className="text-secondary mb-4"
                    style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
                  >
                    {selectedAdDetails.description ||
                      "No description available"}
                  </p>

                  <div className="border-top pt-4">
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Advertiser
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.advertiserName}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Format
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.adFormat}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Media Type
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.mediaType}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Priority
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.priority}
                          </div>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Status
                          </div>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: selectedAdDetails.isActive
                                ? "#e6f7ed"
                                : "#f5f5f5",
                              color: selectedAdDetails.isActive
                                ? "#0f5132"
                                : "#6c757d",
                              fontWeight: "500",
                              padding: "4px 10px",
                              fontSize: "0.85rem",
                            }}
                          >
                            {selectedAdDetails.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Duration
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.durationSeconds
                              ? `${selectedAdDetails.durationSeconds}s`
                              : "—"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12 mt-3">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Campaigns (
                            {selectedAdDetails.campaigns?.length || 0})
                          </div>
                          {selectedAdDetails.campaigns &&
                          selectedAdDetails.campaigns.length > 0 ? (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "6px",
                                  marginBottom: "8px",
                                }}
                              >
                                {(showAllCampaigns
                                  ? selectedAdDetails.campaigns
                                  : selectedAdDetails.campaigns.slice(
                                      0,
                                      MAX_VISIBLE_CAMPAIGNS,
                                    )
                                ).map((camp) => (
                                  <span
                                    key={camp.id}
                                    className="badge"
                                    style={{
                                      backgroundColor: "#e7f3ff",
                                      color: "#0066cc",
                                      fontWeight: "500",
                                      padding: "6px 12px",
                                      fontSize: "0.85rem",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {camp.name} · {camp.adsCount}
                                  </span>
                                ))}
                              </div>
                              {selectedAdDetails.campaigns.length >
                                MAX_VISIBLE_CAMPAIGNS && (
                                <button
                                  className="btn btn-link p-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    textDecoration: "none",
                                  }}
                                  onClick={() =>
                                    setShowAllCampaigns(!showAllCampaigns)
                                  }
                                >
                                  {showAllCampaigns
                                    ? "Show Less"
                                    : `+${
                                        selectedAdDetails.campaigns.length -
                                        MAX_VISIBLE_CAMPAIGNS
                                      } More`}
                                </button>
                              )}
                            </>
                          ) : (
                            <div
                              style={{ fontSize: "0.95rem", color: "#6c757d" }}
                            >
                              No campaigns
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="mb-3">
                          <div
                            className="text-uppercase text-muted mb-1"
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Target
                          </div>
                          <div
                            style={{ fontSize: "0.95rem", color: "#2d3748" }}
                          >
                            {selectedAdDetails.targetAllDevices
                              ? "All Devices"
                              : "Specific Devices"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          ) : null}
        </div>
      </Modal>
      <DeleteModal
        isOpen={isOpenDelete}
        toggle={() => {
          setIsOpenDelete(false);
          setSelectedAd(null);
        }}
        onConfirm={handleDeleteAd}
        title="Delete Media"
        body={
          selectedAd
            ? `Are you sure you want to delete "${selectedAd.title}"? This action cannot be undone.`
            : "Are you sure you want to delete this media?"
        }
      />
    </React.Fragment>
  );
};

export default AdsList;
